import { MouseEvent, useState } from 'react';
import {
	AppBar,
	Box,
	Divider,
	Grid2,
	IconButton,
	Menu,
	MenuItem,
	SvgIcon,
	Typography,
} from '@mui/material';
import { APP_VERSION } from '../appVersion';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';

interface TopNavigationBarProps {
	onClickLogo?: () => void;
	onClickLogout: () => void;
}

function TopNavigationBar(props: TopNavigationBarProps) {
	const [accountMenuAnchor, setAccountMenuAnchor] =
		useState<HTMLElement | null>(null);
	const isAccountMenuOpen = Boolean(accountMenuAnchor);

	function openAccountMenu(event: MouseEvent<HTMLElement>) {
		setAccountMenuAnchor(event.currentTarget);
	}

	function closeAccountMenu() {
		setAccountMenuAnchor(null);
	}

	function logOut() {
		closeAccountMenu();
		props.onClickLogout();
	}

	const brand = (
		<Grid2 container alignItems="center" gap={1.25}>
			<Box
				sx={{
					width: 32,
					height: 32,
					borderRadius: '50%',
					backgroundColor: MAIN_THEME_COLOR,
					color: '#FFFFFF',
					display: 'grid',
					placeItems: 'center',
					fontSize: '12px',
					fontWeight: 700,
					letterSpacing: 0,
				}}>
				CM
			</Box>
			<Typography
				fontSize={{ xs: '14px', sm: '17px' }}
				fontWeight={700}
				color="#111B21"
				whiteSpace="nowrap">
				<Box
					component="span"
					sx={{ display: { xs: 'inline', sm: 'none' } }}>
					SenzMate Bot
				</Box>
				<Box
					component="span"
					sx={{ display: { xs: 'none', sm: 'inline' } }}>
					SenzMate Website Bot Conversation Manager
				</Box>
			</Typography>
		</Grid2>
	);

	return (
		<AppBar
			position="fixed"
			sx={{
				backgroundColor: '#FFFFFF',
				color: '#111B21',
				borderBottom: '1px solid #E9EDEF',
				zIndex: theme => theme.zIndex.drawer + 1,
			}}
			elevation={0}>
			<Grid2
				container
				sx={{ px: { xs: 1.25, sm: 2 } }}
				alignItems={'center'}
				justifyContent={'space-between'}
				height={'60px'}>
				{props.onClickLogo ? (
					<Grid2 container alignItems="center" gap={0.5}>
						<IconButton
							aria-label="Open navigation"
							onClick={props.onClickLogo}
							sx={{ color: '#54656F' }}>
							<Box
								component="span"
								sx={{
									width: 18,
									height: 14,
									display: 'block',
									borderTop: '2px solid currentColor',
									borderBottom: '2px solid currentColor',
									position: 'relative',
									'&::after': {
										content: '""',
										position: 'absolute',
										left: 0,
										right: 0,
										top: 5,
										borderTop: '2px solid currentColor',
									},
								}}
							/>
						</IconButton>
						{brand}
					</Grid2>
				) : (
					brand
				)}
				<IconButton
					aria-label="Open account menu"
					aria-controls={
						isAccountMenuOpen ? 'account-menu' : undefined
					}
					aria-haspopup="true"
					aria-expanded={isAccountMenuOpen ? 'true' : undefined}
					sx={{
						color: '#54656F',
						width: 38,
						height: 38,
						'&:hover': {
							backgroundColor: '#F0F2F5',
							color: MAIN_THEME_COLOR,
						},
					}}
					onClick={openAccountMenu}>
					<SvgIcon viewBox="0 0 24 24">
						<path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.24-8 5v3h16v-3c0-2.76-3.58-5-8-5Z" />
					</SvgIcon>
				</IconButton>
				<Menu
					id="account-menu"
					anchorEl={accountMenuAnchor}
					open={isAccountMenuOpen}
					onClose={closeAccountMenu}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
					PaperProps={{
						sx: { minWidth: 170, mt: 0.75, borderRadius: '6px' },
					}}>
					<MenuItem
						disabled
						sx={{ opacity: '1 !important', py: 1.25 }}>
						<Box>
							<Typography
								fontSize="10px"
								fontWeight={700}
								color="#667781"
								textTransform="uppercase">
								Version
							</Typography>
							<Typography fontSize="13px" color="#111B21">
								{APP_VERSION}
							</Typography>
						</Box>
					</MenuItem>
					<Divider />
					<MenuItem onClick={logOut} sx={{ gap: 1, py: 1.25 }}>
						<SvgIcon fontSize="small" sx={{ color: '#54656F' }}>
							<path d="M10 17v-2h4V9h-4V7l-5 5 5 5Zm9-14H5a2 2 0 0 0-2 2v4h2V5h14v14H5v-4H3v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Z" />
						</SvgIcon>
						<Typography fontSize="13px" fontWeight={600}>
							Log out
						</Typography>
					</MenuItem>
				</Menu>
			</Grid2>
		</AppBar>
	);
}

export default TopNavigationBar;
